import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, Info, Loader2, XIcon } from "lucide-react";
import { toast } from "sonner";

import {
  PINE_SCRIPT_VIEW_MAX_H,
  PineScriptCodeBlock,
} from "../../../components/Reports/PineScriptCodeBlock";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CopyIcon, type CopyIconHandle } from "@/components/ui/copy-icon";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { getStrategyRdPineScript, type StrategyRdPineExport } from "../../../services/strategyRdApi";
import type { StrategyRdBacktestWarning } from "../../../types/strategyRd";
import {
  strategyRdPineChartTimeframeHint,
  strategyRdPineScriptTitle,
} from "../../../utils/strategyRdPineExport";
import { strategyRdRuleFieldErrors } from "../../../utils/strategyRdRuleEditor";

const FIELD_LABELS: Record<string, string> = {
  "stop.rule": "Stop",
  "target.rule": "Target",
  "entry.rule": "Entry",
  "entry.filter": "Setup filter",
  manage: "Manage the trade",
  general: "Recipe",
};

const BODY_TEXT = "font-[family-name:var(--font-primary)] text-sm text-muted-foreground dark:text-white/70";

/** Copy button in the policy dialog's style (animated copy icon). */
function PineCopyButton({ text, label }: { text: string; label: string }): JSX.Element {
  const iconRef = useRef<CopyIconHandle | null>(null);
  const start = useCallback(() => void iconRef.current?.startAnimation(), []);
  const stop = useCallback(() => void iconRef.current?.stopAnimation(), []);
  return (
    <button
      type="button"
      data-policy-view-copy=""
      aria-label={label}
      title={label}
      disabled={!text.trim()}
      onPointerDownCapture={start}
      onMouseEnter={start}
      onMouseLeave={stop}
      onClick={() => {
        void navigator.clipboard
          .writeText(text)
          .then(() => toast.success("Pine Script copied"))
          .catch(() => toast.error("Could not copy, select the script and copy it by hand."));
      }}
    >
      <CopyIcon ref={iconRef} size={16} className="shrink-0 text-current" aria-hidden />
    </button>
  );
}

type PineKind = "strategy" | "indicator";
type PineLoaded = { result?: StrategyRdPineExport; error?: string; fieldErrors?: Record<string, string> };

/** Both scripts (strategy + indicator) for a backtest, loaded together while `active`. */
function useStrategyRdPineScripts(active: boolean, hypothesisId: string | null, name?: string | null) {
  // Switching tabs only shows the other one (it used to clear and rebuild — a flicker).
  const [byKind, setByKind] = useState<Partial<Record<PineKind, PineLoaded>>>({});
  useEffect(() => {
    if (!active || !hypothesisId) return;
    const controller = new AbortController();
    setByKind({});
    (["strategy", "indicator"] as const).forEach((k) => {
      getStrategyRdPineScript(hypothesisId, { kind: k, ...(name ? { name } : {}) }, controller.signal)
        .then((result) => setByKind((prev) => ({ ...prev, [k]: { result } })))
        .catch((err: unknown) => {
          if (err instanceof DOMException && err.name === "AbortError") return;
          const fields = strategyRdRuleFieldErrors(err);
          const loaded: PineLoaded = fields
            ? { fieldErrors: fields as Record<string, string> }
            : { error: err instanceof Error ? err.message : "Could not build the Pine Script." };
          setByKind((prev) => ({ ...prev, [k]: loaded }));
        });
    });
    return () => controller.abort();
  }, [active, hypothesisId, name]);
  return byKind;
}

/**
 * "Get Pine Script" right after a backtest (§11.1) — the same scripts as Save, no strategy
 * created. Built from the last backtest's rules; `stale` says the page has edits since.
 * Styled as the policy Pinescript view (`data-policy-view-*` chrome, branded code block).
 */
export function StrategyRdPineDialog({
  open,
  onOpenChange,
  hypothesisId,
  stale,
  tvSymbol,
  name,
  tradingViewNotes = [],
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hypothesisId: string | null;
  stale: boolean;
  tvSymbol?: string | null;
  name?: string | null;
  /** The backtest's notes on how TradingView will differ (feed, trade count, bar magnifier). */
  tradingViewNotes?: StrategyRdBacktestWarning[];
}): JSX.Element {
  const [kind, setKind] = useState<PineKind>("strategy");
  const byKind = useStrategyRdPineScripts(open, hypothesisId, name);

  const current = byKind[kind];
  const loading = current == null;
  const result = current?.result ?? null;
  const error = current?.error ?? null;
  const fieldErrors = current?.fieldErrors ?? null;
  // The how-to and notes don't change between the two tabs: show whichever loaded first.
  const guide = result ?? byKind.strategy?.result ?? byKind.indicator?.result ?? null;

  const tfHint = strategyRdPineChartTimeframeHint(guide?.chart_timeframe);
  // The script's own feed note repeats the backtest's (shown above): keep one.
  const hasFeedNote = tradingViewNotes.some((w) => w.code === "feed_approximate");
  const guideNotes = (guide?.pine_notes ?? []).filter((n) => !(hasFeedNote && n.startsWith("Expect a different trade count")));
  const tfLabel = tfHint?.match(/^Add to a (.+) chart\.$/)?.[1] ?? null;
  const scripts = useMemo(() => result?.pinescripts ?? [], [result]);
  const single = scripts.length === 1 ? scripts[0]!.script : "";
  const symbol = tvSymbol ? tvSymbol.replace(/^OANDA:/i, "") : "the same symbol";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        data-policy-view-dialog=""
        data-strategy-rd-pine-dialog=""
        className={cn("flex max-h-[min(90vh,48rem)] flex-col sm:max-w-6xl")}
      >
        <DialogClose asChild>
          <button
            type="button"
            data-policy-view-dismiss=""
            aria-label="Close"
            className="absolute top-4 right-4 inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:text-white dark:hover:bg-white/5 dark:hover:text-white"
          >
            <XIcon className="size-4" />
          </button>
        </DialogClose>

        <DialogHeader className="shrink-0">
          <DialogTitle className="font-[family-name:var(--font-primary)] text-base font-normal text-[color:var(--brand-tropical-surf)]">
            Pinescript ({name?.trim() || "last backtest"})
          </DialogTitle>
          <DialogDescription className="font-[family-name:var(--font-primary)] font-normal text-gray-500 dark:text-white">
            The last backtest as a TradingView script. Nothing is saved. Save as strategy whenever you
            want it in your strategies.
          </DialogDescription>
        </DialogHeader>

        <section
          data-strategy-rd-pine-guide=""
          className="max-h-[32vh] shrink-0 overflow-y-auto rounded-[6px] border border-solid border-border bg-card px-4 py-3 dark:bg-sidebar"
        >
          <h4 className="font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]">
            Use it on TradingView
          </h4>
          <ol className={cn(BODY_TEXT, "mt-1.5 list-decimal space-y-0.5 pl-5")}>
            <li>Open {symbol} on TradingView{tfLabel ? ` on a ${tfLabel} chart` : " at the chart timeframe"}.</li>
            <li>Open the Pine Editor, paste the script and click Add to chart.</li>
            <li>The Self-check table (bottom right) shows which trades match this backtest.</li>
          </ol>
          {tradingViewNotes.length > 0 ? (
            <>
              <h4 className="mt-3 font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]">
                How TradingView will differ from this backtest
              </h4>
              <ul className={cn(BODY_TEXT, "mt-1.5 list-disc space-y-1 break-words pl-5")} data-strategy-rd-pine-tv-notes="">
                {tradingViewNotes.map((w) => (
                  <li key={w.code}>
                    <span className="text-gray-500 dark:text-white">{w.title}.</span> {w.message}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {guideNotes.length > 0 ? (
            <>
              <h4 className="mt-3 font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]">
                Good to know
              </h4>
              <ul className={cn(BODY_TEXT, "mt-1.5 list-disc space-y-1 break-words pl-5")}>
                {guideNotes.map((note, i) => (
                  <li key={i}>{note}</li>
                ))}
              </ul>
            </>
          ) : null}
        </section>

        <div className="flex shrink-0 flex-wrap items-center gap-3">
          <Tabs
            data-trade-log-tabs=""
            value={kind}
            onValueChange={(v) => setKind(v as "strategy" | "indicator")}
            className="w-fit shrink-0"
          >
            <TabsList className="min-w-max">
              <TabsTrigger value="strategy">Strategy (backtests on TradingView)</TabsTrigger>
              <TabsTrigger value="indicator">Indicator (signals only)</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div data-strategy-rd-pine-dialog-body="" className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
          {stale ? (
            <Alert variant="warning">
              <Info aria-hidden />
              <AlertDescription className="font-[family-name:var(--font-primary)]">
                You changed the rules since this backtest. Re-run the backtest to include your changes.
              </AlertDescription>
            </Alert>
          ) : null}
          {loading ? (
            <div className="flex items-center gap-3 py-6">
              <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground dark:text-white" aria-hidden />
              <p className={BODY_TEXT}>Building the script…</p>
            </div>
          ) : error ? (
            <Alert variant="error">
              <AlertCircle aria-hidden />
              <AlertDescription className="font-[family-name:var(--font-primary)]">{error}</AlertDescription>
            </Alert>
          ) : fieldErrors ? (
            <Alert variant="error">
              <AlertCircle aria-hidden />
              <AlertDescription className="font-[family-name:var(--font-primary)]">
                <p>TradingView can&apos;t run part of this recipe yet:</p>
                <ul className="mt-1 list-disc pl-5">
                  {Object.entries(fieldErrors).map(([field, reason]) => (
                    <li key={field}>
                      <span className="font-medium">{FIELD_LABELS[field] ?? field}:</span> {reason}
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          ) : scripts.length > 0 ? (
            <>
              {scripts.map((script) => (
                <div key={script.side} className="flex min-w-0 flex-col gap-2" data-strategy-rd-pine-script={script.side}>
                  {scripts.length > 1 ? (
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-[family-name:var(--font-primary)] text-sm text-gray-500 dark:text-white">
                        {strategyRdPineScriptTitle(script.side)}
                      </p>
                      <PineCopyButton text={script.script} label={`Copy ${strategyRdPineScriptTitle(script.side)}`} />
                    </div>
                  ) : null}
                  <PineScriptCodeBlock
                    code={script.script}
                    maxH={PINE_SCRIPT_VIEW_MAX_H}
                    brandChrome
                    chromeTitle={kind === "strategy" ? "Strategy" : "Indicator"}
                    chromeSubtitle="Generated from the last backtest"
                  />
                </div>
              ))}
            </>
          ) : result ? (
            <p className={BODY_TEXT}>No Pinescript returned.</p>
          ) : null}
        </div>

        <DialogFooter className="shrink-0 gap-2 sm:justify-end">
          {single ? <PineCopyButton text={single} label="Copy Pinescript" /> : null}
          <DialogClose asChild>
            <button type="button" data-policy-view-close="">
              Close
            </button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The Pine editor in the Lab's side panel (taskbar → Pine editor): the same scripts as the
 * dialog — strategy / indicator, copy, how TradingView differs — in a narrow column.
 */
export function StrategyRdPinePanel({
  hypothesisId,
  stale,
  tvSymbol,
  name,
  tradingViewNotes = [],
}: {
  hypothesisId: string | null;
  stale: boolean;
  tvSymbol?: string | null;
  name?: string | null;
  tradingViewNotes?: StrategyRdBacktestWarning[];
}): JSX.Element {
  const [kind, setKind] = useState<PineKind>("strategy");
  const byKind = useStrategyRdPineScripts(true, hypothesisId, name);
  if (!hypothesisId) {
    return (
      <p className={cn(BODY_TEXT, "px-4 py-6")} data-strategy-rd-pine-panel="empty">
        Run a backtest first: its rules become the Pine Script here.
      </p>
    );
  }
  const current = byKind[kind];
  const result = current?.result ?? null;
  const guide = result ?? byKind.strategy?.result ?? byKind.indicator?.result ?? null;
  const tfLabel = strategyRdPineChartTimeframeHint(guide?.chart_timeframe)?.match(/^Add to a (.+) chart\.$/)?.[1] ?? null;
  const scripts = result?.pinescripts ?? [];
  const symbol = tvSymbol ? tvSymbol.replace(/^OANDA:/i, "") : "the same symbol";
  return (
    <div className="flex min-h-0 flex-1 flex-col" data-strategy-rd-pine-panel="">
      <div className="flex shrink-0 flex-col gap-2 px-4 pb-3 pt-3">
        <Tabs data-trade-log-tabs="" value={kind} onValueChange={(v) => setKind(v as PineKind)} className="w-full">
          <TabsList className="w-full">
            <TabsTrigger value="strategy" className="flex-1">Strategy</TabsTrigger>
            <TabsTrigger value="indicator" className="flex-1">Indicator</TabsTrigger>
          </TabsList>
        </Tabs>
        {stale ? (
          <Alert variant="warning">
            <Info aria-hidden />
            <AlertDescription className="font-[family-name:var(--font-primary)] text-xs">
              You changed the rules since this backtest. Re-run it to include your changes.
            </AlertDescription>
          </Alert>
        ) : null}
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 pb-4" data-strategy-rd-pine-panel-body="">
        {current == null ? (
          <div className="flex items-center gap-3 py-6">
            <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" aria-hidden />
            <p className={BODY_TEXT}>Building the script…</p>
          </div>
        ) : current.error ? (
          <Alert variant="error">
            <AlertCircle aria-hidden />
            <AlertDescription className="font-[family-name:var(--font-primary)]">{current.error}</AlertDescription>
          </Alert>
        ) : current.fieldErrors ? (
          <Alert variant="error">
            <AlertCircle aria-hidden />
            <AlertDescription className="font-[family-name:var(--font-primary)]">
              <p>TradingView can&apos;t run part of this recipe yet:</p>
              <ul className="mt-1 list-disc pl-5">
                {Object.entries(current.fieldErrors).map(([field, reason]) => (
                  <li key={field}>
                    <span className="font-medium">{FIELD_LABELS[field] ?? field}:</span> {reason}
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        ) : scripts.length > 0 ? (
          scripts.map((script) => (
            <div key={script.side} className="flex min-w-0 flex-col gap-2" data-strategy-rd-pine-script={script.side}>
              <div className="flex items-center justify-between gap-2">
                <p className="font-[family-name:var(--font-primary)] text-sm text-[#4B5563] dark:text-foreground">
                  {scripts.length > 1 ? strategyRdPineScriptTitle(script.side) : kind === "strategy" ? "Strategy" : "Indicator"}
                </p>
                <PineCopyButton text={script.script} label={`Copy ${strategyRdPineScriptTitle(script.side)}`} />
              </div>
              <PineScriptCodeBlock code={script.script} maxH="none" />
            </div>
          ))
        ) : (
          <p className={BODY_TEXT}>No Pinescript returned.</p>
        )}
        <details className="group rounded-[6px] border border-border px-3 py-2" data-strategy-rd-pine-guide="">
          <summary className="cursor-pointer list-none font-[family-name:var(--font-primary)] text-sm text-[color:var(--brand-tropical-surf)]">
            Use it on TradingView
          </summary>
          <ol className={cn(BODY_TEXT, "mt-1.5 list-decimal space-y-0.5 pl-5 text-xs")}>
            <li>Open {symbol} on TradingView{tfLabel ? ` on a ${tfLabel} chart` : " at the chart timeframe"}.</li>
            <li>Open the Pine Editor, paste the script and click Add to chart.</li>
            <li>The Self-check table (bottom right) shows which trades match this backtest.</li>
          </ol>
          {tradingViewNotes.length > 0 ? (
            <ul className={cn(BODY_TEXT, "mt-2 list-disc space-y-1 break-words pl-5 text-xs")}>
              {tradingViewNotes.map((w) => (
                <li key={w.code}>
                  <span className="text-[#4B5563] dark:text-foreground">{w.title}.</span> {w.message}
                </li>
              ))}
            </ul>
          ) : null}
        </details>
      </div>
    </div>
  );
}
